import React, { useEffect, useState } from 'react';
import { isMobileVersion } from '../../lib/soar-helper';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';

import styled from 'styled-components';
import Analytics from '../../lib/user-analytics';
import UriHelper, { useQuery } from '../../lib/uri-helper';
import { regionOptions, regionPolygons, orderByOptions, categoryOptions } from './explore-options';
import ExploreSearch from './explore-search';
import DropDown, { DropOptionType } from '../Shared/dropdown';

export interface FilterValues {
    keywords: string;
    aoi?: string;
    category?: string;
    orderBy?: string;
}

interface ExploreFiltersProps {
    onFilter: (value: FilterValues) => void;
}

const ExploreFilters = ({ onFilter }: ExploreFiltersProps) => {
    const query = useQuery();
    const initialOrderBy = orderByOptions.options.find((o) => o.key === query.get('sort'));
    const paramCategories = query.get('categories')?.split(',');
    const initialCategory = paramCategories
        ? categoryOptions.options.filter((c) => paramCategories?.indexOf(c.value.toLocaleLowerCase()) > -1)
        : [];
    const paramRegions = query.get('regions')?.split(',');
    const initialRegions = paramRegions
        ? regionOptions.options.filter((c) => paramRegions?.indexOf(c.value.toLocaleLowerCase()) > -1)
        : [];
    const initialSearch = query.get('search') ?? '';
    const [searchInput] = useState(() => new Subject<string>());
    const DEBOUNCE_TIME = 250; // milliseconds

    const [orderBy, setOrderBy] = useState<DropOptionType | undefined>(initialOrderBy);
    const [regions, setRegions] = useState<DropOptionType[]>([]);
    const [category, setCategory] = useState<DropOptionType>();
    const [searchTerm, setSearchTerm] = useState<string>(initialSearch);
    const [openDropdown, setOpenDropdown] = useState('');

    useEffect(() => {
        const obj = {
            sort: orderBy?.key,
            search: searchTerm,
            regions: regions.map((c) => c.value.toLocaleLowerCase()).join(','),
        };
        UriHelper.addParametersToUri(obj);

        let aoi: string | undefined = undefined;
        if (regions?.length > 0) {
            aoi = `${regions.length > 1 ? 'MULTIPOLYGON (' : 'POLYGON'}${regions
                .map((r) => regionPolygons[r.value])
                .join(',')}${regions.length > 1 ? ')' : ''}`;
        }

        onFilter({
            keywords: searchTerm,
            aoi: aoi,
            orderBy: orderBy?.value,
            category: category ? category.value : undefined,
        });
        if (searchTerm) {
            Analytics.Event('Explore', 'Changed search term', searchTerm);
        }
    }, [searchTerm, orderBy, regions, category, onFilter]);

    useEffect(() => {
        const subscription = searchInput
            .pipe(
                filter((value) => value.length >= 0),
                debounceTime(DEBOUNCE_TIME),
                distinctUntilChanged(),
                switchMap((value) => (value.length >= 0 ? Promise.resolve<string>(value) : Promise.resolve<string>('')))
            )
            .subscribe((next) => {
                setSearchTerm(next);
            });

        return () => {
            subscription.unsubscribe();
        };
    }, [searchInput]);

    const mobileDropdownContainer = (
        <ExploreMobileDropDownContainer>
            <DropDown
                name="OrderBy"
                options={orderByOptions.options}
                title={orderByOptions.title}
                initial={initialOrderBy ? [initialOrderBy] : [orderByOptions.options[0]]}
                handleSelectedOptions={(order) => setOrderBy(order[0])}
                isRadio={true}
                isOpen={openDropdown === 'OrderBy'}
                handleOpen={() => setOpenDropdown('OrderBy')}
                icons={{
                    light: orderByOptions.icons.light,
                    dark: orderByOptions.icons.dark,
                }}
            />
        </ExploreMobileDropDownContainer>
    );

    const dropdownContainers = (
        <ExploreDropDownContainer>
            <DropDown
                name="OrderBy"
                options={orderByOptions.options}
                title={orderByOptions.title}
                initial={initialOrderBy ? [initialOrderBy] : [orderByOptions.options[0]]}
                handleSelectedOptions={(order) => setOrderBy(order[0])}
                isRadio={true}
                isOpen={openDropdown === 'OrderBy'}
                handleOpen={() => setOpenDropdown('OrderBy')}
                icons={{
                    light: orderByOptions.icons.light,
                    dark: orderByOptions.icons.dark,
                }}
            />
            <DropDown
                name="Category"
                options={categoryOptions.options}
                title={categoryOptions.title}
                initial={initialCategory}
                handleSelectedOptions={(category) => setCategory(category[0])}
                isRadio={true}
                categoryFilter
                isOpen={openDropdown === 'Category'}
                icons={{ light: categoryOptions.icons.light, dark: categoryOptions.icons.dark }}
                handleOpen={() => setOpenDropdown('Category')}
            />
            <DropDown
                name="Region"
                options={regionOptions.options}
                title={regionOptions.title}
                initial={initialRegions}
                handleSelectedOptions={(regions) => setRegions(regions)}
                isOpen={openDropdown === 'Region'}
                icons={{ light: regionOptions.icons.light, dark: regionOptions.icons.dark }}
                handleOpen={() => setOpenDropdown('Region')}
            />
        </ExploreDropDownContainer>
    );

    return (
        <ExploreSearchBarContainer>
            <ExploreFilterContainer>
                <ExploreSearch
                    handleKeywordSearch={(term) => searchInput.next(term)}
                    resetCategory={() => setOpenDropdown('')}
                    searchTerm={initialSearch}
                />
            </ExploreFilterContainer>
            {isMobileVersion ? (
                <React.Fragment>{mobileDropdownContainer}</React.Fragment>
            ) : (
                <React.Fragment>{dropdownContainers}</React.Fragment>
            )}
        </ExploreSearchBarContainer>
    );
};

export default ExploreFilters;

const ExploreSearchBarContainer = styled.div`
    display: flex;
    flex-direction: row;
`;

const ExploreFilterContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 60vw;

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`;

const ExploreDropDownContainer = styled.div`
    width: 60vw;
    display: flex;
    padding: 0px 20px 0px 10px;
    gap: 7px;
    align-items: center;
`;

const ExploreMobileDropDownContainer = styled.div`
    margin: 5px 0px;

    @media only screen and (max-width: 600px) {
        margin: 0px 11px 0px 0px;
    }
`;
